import * as React from "react";
import { merge, $ } from "glamor";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import fetch from "lib/fetch";
import Dialog from "components/dialog/dialog";
import Button from "components/form/button";
import { format } from "lib/globalize-util";
import { FormatMessage } from "react-globalize";
import theme from "styles/theme";
import moment from "moment";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { IItemRecord } from "records/item/item";
import { IShipDate } from "records/order";
import { SNACK_ID } from "./build-order-utils";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import type { IScope } from "reducers/scope";
import { getSwatchLabel } from "helpers/swatch-utils";

interface IWindow {
    start: number;
    end: number;
}

interface IFullWindow {
    deliveryWindow: IWindow;
    params: object;
}

interface IProps {
    open: boolean;
    onRequestClose: () => void;
    onCancel: () => void;
    handleItemAdd: (item: IItemRecord, params: any) => void;
    item: IItemRecord;
    windows: IFullWindow[];
    shipDates: IShipDate[];
    onSnackbarAdd: (message: object) => void;
    onSnackbarRemove?: (id: string) => void;
    orderId: string;
    scope: IScope;
}

const NewShipmentModal: React.FC<IProps> = (props) => {
    const {
        open,
        onRequestClose,
        onCancel,
        handleItemAdd,
        item,
        windows,
        shipDates,
        orderId,
        onSnackbarAdd,
        onSnackbarRemove,
        scope,
    } = props;

    const styles = React.useMemo(
        () => ({
            body: {
                overflow: "unset",
                paddingTop: "18px",
                color: theme.palette.textColor,
            },
            contentStyle: {
                minWidth: "200px",
            },
            actionsContainerStyle: {
                display: "flex",
                justifyContent: "flex-end",
            },
            btnYes: {
                marginLeft: "10px",
            },
        }),
        []
    );

    const contentStyles = React.useMemo(() => {
        return {
            titleIcon: merge({
                fontSize: "1.8rem !important",
                marginRight: "8px",
            }),
            content: merge(
                $(" .productRow", {
                    display: "flex",
                    flexFlow: "row nowrap",
                    justifyContent: "flex-start",
                }),
                $(" .productImg", {
                    flex: "0 1 auto",
                    marginRight: "12px",
                    "& img": {
                        maxHeight: "180px",
                    },
                }),
                $(" .newDate", {
                    fontWeight: "bold",
                }),
                $(" .productMeta", {
                    display: "flex",
                    flexFlow: "column nowrap",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    " .productName": {
                        fontWeight: "bold",
                    },
                })
            ),
        };
    }, []);

    const handleCloseSnack = () => onSnackbarRemove(SNACK_ID);

    const handleConfirm = React.useCallback(() => {
        const newShipDates = [
            ...shipDates,
            ...windows.map((window) => ({
                ship_start: moment.utc(window.deliveryWindow.start).format("YYYY-MM-DD"),
                ship_end: moment.utc(window.deliveryWindow.end).format("YYYY-MM-DD"),
                line_items: [],
            })),
        ];
        newShipDates.sort((a, b) => moment(a.ship_start).valueOf() - moment(b.ship_start).valueOf());
        fetch(`/working/update/${orderId}`, {
            method: "POST",
            body: JSON.stringify({
                __build_order: {
                    ship_dates: newShipDates,
                },
            }),
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(format("Problem updating order. Please refresh and try again."));
                }
            })
            .then((data) => {
                windows.forEach((window) => {
                    const newParams = {
                        ...window.params,
                        ship_start: window.deliveryWindow.start,
                        ship_end: window.deliveryWindow.end,
                        message: format("Product(s) added to cart"),
                    };
                    handleItemAdd(item, newParams);
                });
                onSnackbarAdd({
                    id: SNACK_ID,
                    message: format("Shipment date(s) updated"),
                    shouldUseLatestSnack: true,
                    action: (
                        <React.Fragment>
                            <IconButton size="small" color="inherit" onClick={handleCloseSnack}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </React.Fragment>
                    ),
                    messageStyle: {
                        fontSize: 14,
                        fontFamily: theme.fontFamily,
                    },
                });
                onRequestClose();
            })
            .catch((error: Error) => {
                onSnackbarAdd({ message: error.message });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleItemAdd, item, windows, shipDates, orderId, onSnackbarAdd, onRequestClose]);

    return (
        <MuiThemeProvider muiTheme={theme}>
            <Dialog
                title={
                    <div>
                        <CalendarTodayIcon
                            htmlColor={theme.palette.primary1Color}
                            className={`${contentStyles.titleIcon}`}
                        />
                        {format("Create New Shipment?")}
                    </div>
                }
                open={open}
                onRequestClose={onRequestClose}
                contentStyle={styles.contentStyle}
                actionsContainerStyle={styles.actionsContainerStyle}
                bodyStyle={styles.body}
                actions={[
                    <Button key="no" label={format("No, don't add")} onClick={onCancel} />,
                    <Button
                        key="yes"
                        label={format("Yes, add shipment")}
                        style={styles.btnYes}
                        onClick={handleConfirm}
                        primary={true}
                    />,
                ]}
            >
                <div {...contentStyles.content}>
                    <div className="modalText">
                        <FormatMessage
                            variables={{
                                date: windows
                                    .map((window) => moment.utc(window?.deliveryWindow.start).format("MM/DD/YYYY"))
                                    .join(", "),
                            }}
                            elements={{ span: <span className="newDate" /> }}
                        >
                            This product is available outside of the shipments you selected. Ordering this product will
                            add [span]|date|[span] as a new ship date. Would you like to continue?
                        </FormatMessage>
                    </div>
                    <div className="productRow">
                        <div className="productImg">
                            <img src={item.getImageUrl(scope, "M", 0)} />
                        </div>
                        <div className="productMeta">
                            <div className="productName">{item.name}</div>
                            <div>{item.style_number}</div>
                            <div>{getSwatchLabel(item, scope)}</div>
                            <div>{`${format("Avail")}: ${item.__available_from?.format("MM/DD/YY")} - ${
                                item.__available_until?.format("MM/DD/YY") ?? ""
                            }`}</div>
                        </div>
                    </div>
                    <hr />
                </div>
            </Dialog>
        </MuiThemeProvider>
    );
};

export { NewShipmentModal };
